import { shaderMaterial } from '@react-three/drei';
import * as THREE from 'three';

export const WaterShaderMaterial = shaderMaterial(
  {
    uTime: 0,
    // Wave controls
    uWaveSpeed: 0.5,
    uWaveAmplitude: 0.25,
    // Colors
    //OLD
    // uWaterColor: new THREE.Color('#080810'),
    // uRimColor: new THREE.Color('#1a4d5c'),
    // uCausticColor: new THREE.Color('#2a6a7a'),

    uWaterColor: new THREE.Color('#b31717'),
    uRimColor: new THREE.Color('#1a4d5c'),
    uCausticColor: new THREE.Color('#12a2b9'),
    // Fresnel (rim glow)
    uFresnelPower: 1.0, // how tight the rim is (higher = thinner edge)
    uRimStrength: 2.2, // brightness of the rim glow
    uRimBlend: 0.4, // how much rim color bleeds into the base
    // Caustics
    uCausticIntensity: 5.6, // brightness of caustic patterns
    uCausticSharpness: 3.0, // how sharp/concentrated the caustic spots are
    // Specular
    uSpecularStrength: 0.8, // brightness of specular highlights
    uSpecularPower: 12.0, // tightness of specular highlights
    // Displacement color
    uDisplacementColorStrength: 5.0, // how much waves affect surface color
    // Mouse light
    uMouseLight: new THREE.Vector3(0, 0, 5), // world-space cursor light position
    uMouseLightColor: new THREE.Color('#4a8a9a'), // color of the cursor light
    // OLD
    //uMouseLightIntensity: 2.5, // brightness of the cursor light
    uMouseLightIntensity: 1.5, // brightness of the cursor light
    uAlpha: 1.0, // overall opacity (used for reflection)
  },
  // Vertex Shader
  /* glsl */ `
    uniform float uTime;
    uniform float uWaveSpeed;
    uniform float uWaveAmplitude;

    varying vec3 vNormal;
    varying vec3 vPosition;
    varying vec2 vUv;
    varying float vDisplacement;

    // Reusable wave function so we can sample at offset positions for normal calculation
    float getDisplacement(vec3 pos, float t) {
      float w1 = sin(pos.x * 3.0 + t * 2.0) * cos(pos.z * 2.5 + t * 1.5);
      float w2 = sin(pos.y * 4.0 + t * 1.8) * cos(pos.x * 3.5 - t * 1.2);
      float w3 = sin(pos.z * 5.0 - t * 2.2) * sin(pos.y * 2.0 + t * 0.8);
      float w4 = cos(pos.x * 6.0 + pos.z * 4.0 + t * 1.0) * 0.5;
      return (w1 + w2 + w3 + w4) * 0.25 * uWaveAmplitude;
    }

    void main() {
      vUv = uv;

      float t = uTime * uWaveSpeed;
      float displacement = getDisplacement(position, t);
      vDisplacement = displacement;

      vec3 newPosition = position + normal * displacement;

      // Compute displaced normal via finite differences
      float eps = 0.01;
      // Get two tangent directions on the sphere surface
      vec3 tangent1 = normalize(cross(normal, vec3(0.0, 1.0, 0.0)));
      // If normal is nearly parallel to up, use a different reference
      if (length(cross(normal, vec3(0.0, 1.0, 0.0))) < 0.001) {
        tangent1 = normalize(cross(normal, vec3(1.0, 0.0, 0.0)));
      }
      vec3 tangent2 = normalize(cross(normal, tangent1));

      // Sample displacement at neighboring points
      vec3 posT1 = position + tangent1 * eps;
      vec3 posT2 = position + tangent2 * eps;
      float dispT1 = getDisplacement(posT1, t);
      float dispT2 = getDisplacement(posT2, t);

      vec3 neighborT1 = posT1 + normal * dispT1;
      vec3 neighborT2 = posT2 + normal * dispT2;

      vec3 displacedNormal = normalize(cross(neighborT1 - newPosition, neighborT2 - newPosition));

      vNormal = normalize(normalMatrix * displacedNormal);
      vPosition = (modelMatrix * vec4(newPosition, 1.0)).xyz;

      gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
    }
  `,
  // Fragment Shader
  /* glsl */ `
    uniform float uTime;
    uniform vec3 uWaterColor;
    uniform vec3 uRimColor;
    uniform vec3 uCausticColor;
    uniform float uFresnelPower;
    uniform float uRimStrength;
    uniform float uRimBlend;
    uniform float uCausticIntensity;
    uniform float uCausticSharpness;
    uniform float uSpecularStrength;
    uniform float uSpecularPower;
    uniform float uDisplacementColorStrength;
    uniform vec3 uMouseLight;
    uniform vec3 uMouseLightColor;
    uniform float uMouseLightIntensity;
    uniform float uAlpha;

    varying vec3 vNormal;
    varying vec3 vPosition;
    varying vec2 vUv;
    varying float vDisplacement;

    void main() {
      vec3 viewDir = normalize(cameraPosition - vPosition);
      float fresnel = pow(1.0 - max(dot(vNormal, viewDir), 0.0), uFresnelPower);

      // --- Cursor light (the sole light source) ---
      vec3 lightDir = normalize(uMouseLight - vPosition);
      float diffuse = max(dot(vNormal, lightDir), 0.0);
      float dist = length(uMouseLight - vPosition);
      float attenuation = 1.0 / (1.0 + dist * dist * 0.15);
      float light = diffuse * attenuation * uMouseLightIntensity;

      // Ripple/wave patterns (using world position to avoid UV seam)
      float t = uTime * 0.3;
      vec3 p = vPosition;

      float ripple1 = sin(p.x * 4.0 + p.y * 2.0 + t * 2.0);
      float ripple2 = sin(p.y * 3.5 - p.z * 3.0 + t * 1.7);
      float ripple3 = sin(p.z * 4.5 + p.x * 1.5 - t * 1.3);
      float ripple4 = sin(p.x * 2.0 + p.z * 3.0 + p.y * 1.0 + t * 0.9);

      float caustic = (ripple1 + ripple2 + ripple3 + ripple4) * 0.25;
      caustic = caustic * 0.5 + 0.5;
      caustic = pow(caustic, uCausticSharpness) * uCausticIntensity;

      // Surface color (what the light reveals)
      vec3 surfaceColor = uWaterColor;
      surfaceColor += uCausticColor * caustic;
      surfaceColor += vec3(0.1, 0.15, 0.25) * vDisplacement * uDisplacementColorStrength;

      // Rim glow — also driven by cursor proximity
      vec3 rimColor = uRimColor * fresnel * uRimStrength * attenuation * uMouseLightIntensity;

      // Specular highlight from cursor light
      float spec = pow(max(dot(reflect(-lightDir, vNormal), viewDir), 0.0), uSpecularPower);
      vec3 specColor = uMouseLightColor * spec * uSpecularStrength * attenuation;

      // Everything is lit by the cursor — dark when cursor is far away
      vec3 finalColor = surfaceColor * light + rimColor + specColor;

      gl_FragColor = vec4(finalColor, uAlpha);
    }
  `,
);
